import { C } from './shapes'

/**
 * Kleines Spielfeld für den Abschluss: Aivy-Formen fallen auf den Boden,
 * stapeln sich, rollen und lassen sich mit dem Mauszeiger anschubsen.
 * Kollision über Kreise, gezeichnet wird die echte Form. Läuft nur, solange
 * das Feld sichtbar ist.
 */

const KINDS = ['circle', 'square', 'tri', 'half', 'quarter']
const COLORS = [C.blue, C.green, C.yellow, C.pink, C.markBlue, C.teal]
const GRAVITY = 1800
const REPEL = 120

function rounded(ctx, corners, rad, round) {
  const pts = []
  for (let k = 0; k < corners; k++) {
    const a =
      -Math.PI / 2 +
      (k * Math.PI * 2) / corners +
      (corners === 4 ? Math.PI / 4 : 0)
    pts.push([Math.cos(a) * rad, Math.sin(a) * rad])
  }
  ctx.beginPath()
  for (let k = 0; k < corners; k++) {
    const [x, y] = pts[k]
    const [px, py] = pts[(k + corners - 1) % corners]
    const [nx, ny] = pts[(k + 1) % corners]
    const ax = x + (px - x) * round
    const ay = y + (py - y) * round
    if (k === 0) ctx.moveTo(ax, ay)
    else ctx.lineTo(ax, ay)
    ctx.quadraticCurveTo(x, y, x + (nx - x) * round, y + (ny - y) * round)
  }
  ctx.closePath()
}

function draw(ctx, b) {
  ctx.save()
  ctx.translate(b.x, b.y)
  ctx.rotate(b.a)
  ctx.fillStyle = b.color
  const r = b.r
  if (b.kind === 'circle') {
    ctx.beginPath()
    ctx.arc(0, 0, r, 0, Math.PI * 2)
  } else if (b.kind === 'square') {
    rounded(ctx, 4, r * 1.25, 0.2)
  } else if (b.kind === 'tri') {
    // Schwerpunkt liegt tiefer, damit das Dreieck sauber aufliegt
    ctx.translate(0, r * 0.18)
    rounded(ctx, 3, r * 1.32, 0.16)
  } else if (b.kind === 'half') {
    ctx.beginPath()
    ctx.moveTo(-r * 1.15, r * 0.45)
    ctx.arc(0, r * 0.45, r * 1.15, Math.PI, 0)
    ctx.closePath()
  } else {
    ctx.beginPath()
    ctx.moveTo(-r * 0.75, r * 0.75)
    ctx.arc(-r * 0.75, r * 0.75, r * 1.6, -Math.PI / 2, 0)
    ctx.closePath()
  }
  ctx.fill()
  ctx.restore()
}

export class Pit {
  constructor(canvas) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')
    this.bodies = []
    this.mouse = { x: -9999, y: -9999 }
    this.queue = 0
    this.seed = 1
    this.resize()
  }

  rand() {
    this.seed = (this.seed * 16807) % 2147483647
    return (this.seed - 1) / 2147483646
  }

  resize() {
    const r = this.canvas.getBoundingClientRect()
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    this.w = r.width
    this.h = r.height
    this.canvas.width = Math.round(r.width * dpr)
    this.canvas.height = Math.round(r.height * dpr)
    this.dpr = dpr
    this.scale = Math.max(0.6, Math.min(1.1, r.width / 1300))
  }

  /** Füllt das Feld nach und nach, `count` Formen insgesamt. */
  fill(count) {
    this.queue = Math.max(0, count - this.bodies.length)
  }

  spawn(x, y, burst = false) {
    const r = (16 + this.rand() * 26) * this.scale
    this.bodies.push({
      x: x ?? r + this.rand() * (this.w - 2 * r),
      y: y ?? -r - this.rand() * 80,
      vx: burst ? (this.rand() - 0.5) * 600 : (this.rand() - 0.5) * 80,
      vy: burst ? -300 - this.rand() * 400 : 0,
      r,
      a: this.rand() * Math.PI * 2,
      kind: KINDS[Math.floor(this.rand() * KINDS.length)],
      color: COLORS[Math.floor(this.rand() * COLORS.length)]
    })
    // Nicht unendlich wachsen lassen
    if (this.bodies.length > 140) this.bodies.shift()
  }

  point(x, y) {
    this.mouse.x = x
    this.mouse.y = y
  }

  step(dt) {
    const { bodies, w, h, mouse } = this
    if (this.queue > 0 && this.rand() < 0.35) {
      this.spawn()
      this.queue--
    }
    for (const b of bodies) {
      b.vy += GRAVITY * dt
      const mx = b.x - mouse.x
      const my = b.y - mouse.y
      const d2 = mx * mx + my * my
      if (d2 < REPEL * REPEL) {
        const d = Math.sqrt(d2) || 1
        const f = (1 - d / REPEL) * 5200 * dt
        b.vx += (mx / d) * f
        b.vy += (my / d) * f
      }
      b.x += b.vx * dt
      b.y += b.vy * dt
    }
    for (let it = 0; it < 3; it++) {
      for (let i = 0; i < bodies.length; i++) {
        const a = bodies[i]
        for (let j = i + 1; j < bodies.length; j++) {
          const b = bodies[j]
          const dx = b.x - a.x
          const dy = b.y - a.y
          const min = a.r + b.r
          const d2 = dx * dx + dy * dy
          if (d2 >= min * min || d2 === 0) continue
          const d = Math.sqrt(d2)
          const nx = dx / d
          const ny = dy / d
          const push = (min - d) / 2
          a.x -= nx * push
          a.y -= ny * push
          b.x += nx * push
          b.y += ny * push
          const rv = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny
          if (rv < 0) {
            const imp = -rv * 0.6
            a.vx -= nx * imp
            a.vy -= ny * imp
            b.vx += nx * imp
            b.vy += ny * imp
          }
        }
      }
      for (const b of bodies) {
        if (b.y > h - b.r) {
          b.y = h - b.r
          if (b.vy > 0) b.vy *= -0.25
          b.vx *= 0.96
        }
        if (b.x < b.r) {
          b.x = b.r
          b.vx = Math.abs(b.vx) * 0.4
        } else if (b.x > w - b.r) {
          b.x = w - b.r
          b.vx = -Math.abs(b.vx) * 0.4
        }
      }
    }
    // Rollen: Drehung folgt der Bewegung über den Boden
    for (const b of bodies) {
      b.vx *= 0.995
      b.a += (b.vx / b.r) * dt
    }
  }

  render() {
    const { ctx } = this
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    ctx.clearRect(0, 0, this.w, this.h)
    for (const b of this.bodies) draw(ctx, b)
  }
}
